<script setup lang="ts">
import SectionTitle from '@/components/atoms/SectionTitle.vue'
import { computed, ref } from 'vue'
import {
  useCompleteUploadKollegeReceiptMutation,
  useIssueDownloadKollegeReceiptUrlMutation,
  useIssueUploadKollegeReceiptUrlMutation,
  useKollegePaymentViewQuery,
} from './PaymentView.generated'
import { useDropZone } from '@vueuse/core'
import FlatButton from '@/components/atoms/FlatButton.vue'
import { useAlert } from '@/libs/alertPortal'

const props = defineProps<{
  userId: string
}>()

const query = useKollegePaymentViewQuery({ userId: props.userId })

const payment = computed(() => {
  if (!query.result.value?.kollegeUser?.standardCourse?.payment) return null

  return query.result.value.kollegeUser.standardCourse.payment
})

const alert = useAlert()

const receiptPDFInputRef = ref<HTMLInputElement>()
const receiptPDFDropZoneRef = ref<HTMLDivElement>()

const dropZone = useDropZone(receiptPDFDropZoneRef, {
  multiple: false,
  dataTypes: ['application/pdf'],
  onDrop: files => {
    if (files == null) return
    if (files.length === 0) return

    const file = files[0]

    if (file.type !== 'application/pdf') {
      alert.error('PDF ファイルを選択してください')
      return
    }

    receiptPDFFile.value = {
      file,
      url: URL.createObjectURL(file),
    }
  },
})

const handleReceiptPDFInputClick = () => {
  receiptPDFInputRef.value?.click()
}

const handleReceiptPDFInputClearButtonClick = () => {
  receiptPDFFile.value = undefined
}

const handleReceiptPDFInputChange = (event: Event) => {
  const files = (event.target as HTMLInputElement).files
  if (!files) return
  if (files.length === 0) return

  const file = files[0]

  if (file.type !== 'application/pdf') {
    alert.error('PDF ファイルを選択してください')
    return
  }

  receiptPDFFile.value = {
    file: files[0],
    url: URL.createObjectURL(files[0]),
  }
}

const receiptPDFFile = ref<{ file: File; url: string }>()

const issueUploadKollegeReceiptUrlMutation =
  useIssueUploadKollegeReceiptUrlMutation({})

const completeUploadKollegeReceiptMutation =
  useCompleteUploadKollegeReceiptMutation({})

const isUploading = ref(false)

const handleUploadButtonClick = async () => {
  if (!receiptPDFFile.value) return
  if (!payment.value) return

  if (
    !window.confirm(
      '領収書をアップロードしますか？\nアップロード後は取り消すことができません。'
    )
  ) {
    return
  }

  isUploading.value = true

  const { file } = receiptPDFFile.value

  try {
    const issueUrlResult = await issueUploadKollegeReceiptUrlMutation.mutate({
      input: {
        paymentId: payment.value.id,
      },
    })
    if (issueUrlResult?.errors) {
      throw new Error(issueUrlResult.errors[0].message)
    }

    const presignedUrl =
      issueUrlResult?.data?.issueUploadKollegeReceiptUrl.presignedUrl
    if (!presignedUrl) {
      throw new Error('presignedUrl is null')
    }

    await fetch(presignedUrl, {
      method: 'PUT',
      body: file,
    })

    const completeUploadResult =
      await completeUploadKollegeReceiptMutation.mutate({
        input: {
          paymentId: payment.value.id,
        },
      })
    if (completeUploadResult?.errors) {
      throw new Error(completeUploadResult.errors[0].message)
    }
  } catch (error) {
    console.error(error)
    alert.error('領収書のアップロードに失敗しました')
    return
  } finally {
    isUploading.value = false
  }

  alert.success('領収書のアップロードに成功しました')

  query.refetch()
}

const issueDownloadKollegeReceiptUrlMutation =
  useIssueDownloadKollegeReceiptUrlMutation({})

const isDownloading = ref(false)

const handleDownloadReceiptButtonClick = async () => {
  if (!payment.value) return
  if (!payment.value.isUploadedReceipt) return

  isDownloading.value = true

  try {
    const issueDownloadUrlResult =
      await issueDownloadKollegeReceiptUrlMutation.mutate({
        input: {
          paymentId: payment.value.id,
        },
      })
    if (issueDownloadUrlResult?.errors) {
      throw new Error(issueDownloadUrlResult.errors[0].message)
    }

    const presignedUrl =
      issueDownloadUrlResult?.data?.issueDownloadKollegeReceiptUrl.presignedUrl
    if (!presignedUrl) {
      throw new Error('presignedUrl is null')
    }

    const a = document.createElement('a')
    a.href = presignedUrl
    a.download = '領収書.pdf'
    a.target = '_blank'
    document.body.appendChild(a)
    a.click()
    document.body.removeChild(a)
  } catch (error) {
    console.error(error)
    alert.error('領収書のダウンロードに失敗しました')
    return
  } finally {
    isDownloading.value = false
  }
}
</script>

<template>
  <section :class="$style.main" v-if="payment">
    <SectionTitle :class="$style.title">支払い方法</SectionTitle>
    <div :class="$style.content">
      <section v-if="payment.fincodeOrderURL" :class="$style.section">
        <h1 :class="$style.subTitle">Fincode</h1>
        <div :class="$style.content">
          <a
            :href="payment.fincodeOrderURL"
            target="_blank"
            rel="noopener noreferrer"
          >
            決済詳細ページ
          </a>
        </div>
      </section>
      <section :class="$style.section">
        <h1 :class="$style.subTitle">領収書</h1>
        <div :class="$style.content" v-if="payment.isUploadedReceipt">
          <div>
            <FlatButton
              @click="handleDownloadReceiptButtonClick"
              :disabled="isDownloading"
            >
              領収書をダウンロードする
            </FlatButton>

            <p :class="$style.revokeInfo">
              <span :style="{ fontWeight: 'bold' }">
                アップロードの取り消しについて：</span
              ><br />
              誤ったファイルをアップロードしたなど、領収書を取り消したい場合は開発チームにご連絡をお願いします。
            </p>
          </div>
        </div>
        <div v-else>
          <div v-if="receiptPDFFile">
            <div>
              <div :class="$style.receiptPDFInfo">
                <p>
                  入力されたファイル：
                  <code :class="$style.fileName">{{
                    receiptPDFFile.file.name
                  }}</code>
                </p>
                <FlatButton
                  variant="base"
                  size="small"
                  @click="handleReceiptPDFInputClearButtonClick"
                >
                  取り消す
                </FlatButton>
              </div>
              <iframe
                :src="receiptPDFFile.url"
                :class="$style.receiptPDFFrame"
              />
              <div :class="$style.uploadButtonWrapper">
                <FlatButton
                  @click="handleUploadButtonClick"
                  :disabled="isUploading"
                >
                  アップロードする
                </FlatButton>
              </div>
            </div>
          </div>
          <div v-else>
            <div
              ref="receiptPDFDropZoneRef"
              :class="[
                $style.dropZone,
                {
                  [$style['dropZone--over']]: dropZone.isOverDropZone.value,
                },
              ]"
              @click="handleReceiptPDFInputClick"
            >
              <input
                ref="receiptPDFInputRef"
                type="file"
                accept="application/pdf"
                multiple="false"
                style="display: none"
                @change="handleReceiptPDFInputChange"
              />
              <div>領収書の PDF ファイルを入力してください</div>
            </div>
          </div>
        </div>
      </section>
    </div>
  </section>
</template>

<style lang="scss" module>
.main {
  display: flex;
  flex-direction: column;
  gap: 24px;
}

.section {
  display: flex;
  flex-direction: column;
  gap: 24px;
  font-size: 16px;
}

.title {
  padding-bottom: 24px;
  border-bottom: 1px solid $color-border-low-emphasis;
}

.content {
  display: flex;
  flex-direction: column;
  gap: 64px;
}

.subTitle {
  font-size: 20px;
  font-weight: 500;
}

.dropZone {
  width: 100%;
  height: 200px;
  border: 2px dashed $color-border-low-emphasis;
  border-radius: 8px;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 18px;
  font-weight: 500;
  color: $color-text-and-object-low-emphasis;
  cursor: pointer;

  &:hover,
  &--over {
    border-color: $color-border-accent-primary;
    color: $color-text-and-object-accent-primary;
  }
}

.receiptPDFInfo {
  display: flex;
  align-items: center;
  gap: 16px;
}

.fileName {
  padding: 0.2em 0.4em;
  margin: 0;
  font-size: 85%;
  white-space: break-spaces;
  background-color: $color-background-base;
  border-radius: 6px;
}

.receiptPDFFrame {
  margin-top: 16px;
  width: 100%;
  height: 600px;
}

.uploadButtonWrapper {
  margin-top: 16px;
}

.revokeInfo {
  line-height: 1.8;
  margin-top: 24px;
  font-size: 14px;
  background-color: $color-background-base;
  border: 1px solid $color-border-low-emphasis;
  padding: 16px;
  border-radius: 8px;
}
</style>
